import React, { useMemo } from 'react';
import { TransitionGroup } from 'react-transition-group';
import styled, { css } from 'styled-components';

import { Target } from '../../../../client';
import { ViewportWidthRef } from '../../../../constants/ui/viewport';
import { type SportId } from '../../../../types/sports';
import { LineUpFormula1StartingTrack, LineUpFormula1TrackRepeat } from '../../assets/img';
import { LineUpRaceTrackBySportId } from '../../constants/ui';
import LineUpRacerContainer from '../LineUpRacer/LineUpRacerContainer';
import LineUpRaceStatusIndicatorContainer from '../LineUpRaceStatusIndicator/LineUpRaceStatusIndicatorContainer';

const TRACK_PADDING = 1; // em
const TRACK_TOP_OFFSET = 15.5; // em
const DESKTOP_TRACK_PADDING = 0.75; // em
const DESKTOP_TRACK_TOP_OFFSET = 19; // em

const Wrapper = styled.div`
  width: 100%;
  min-height: 100%;
  position: relative;

  @media (max-width: ${ViewportWidthRef}px) {
    font-size: 11px;
  }

  transform-style: preserve-3d;

  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const RaceStatusIndicator = styled(LineUpRaceStatusIndicatorContainer)`
  width: 100%;
  height: 1.33em;
  font-size: ${Target.__MOBILE__ ? '100%' : '130%'};
  margin: 1em 0 4.6em;

  display: flex;
  align-items: center;
  justify-content: center;
`;

const TrackWrapperBackground = styled.div`
  position: absolute;
  top: -${Target.__MOBILE__ ? TRACK_TOP_OFFSET : DESKTOP_TRACK_TOP_OFFSET}em;
  left: -${Target.__MOBILE__ ? TRACK_PADDING : DESKTOP_TRACK_PADDING}em;
  transform: translateZ(-1px);
  width: calc(100% + 2 * ${Target.__MOBILE__ ? TRACK_PADDING : DESKTOP_TRACK_PADDING}em);
  height: calc(100% + ${Target.__MOBILE__ ? TRACK_TOP_OFFSET : DESKTOP_TRACK_TOP_OFFSET}em);

  background-image: url(${LineUpFormula1TrackRepeat});
  background-size: 100%;
  background-repeat: repeat;
`;

const TrackBackground = styled.div`
  width: 100%;
  height: 100%;

  background-image: url(${LineUpFormula1StartingTrack});
  background-size: 100%;
  background-repeat: no-repeat;

  transform: translateZ(-100px);
`;

const Track = styled.div`
  margin: 0 0.1em;
  padding: 0 0.1em 1em;

  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

type CarsWrapperType = {
  $padding?: string;
};

const CarsWrapper = styled.div<CarsWrapperType>`
  width: 100%;

  ${({ $padding }) => css`
    padding: ${$padding};
  `}

  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-direction: row;
`;

type LineType = {
  $paddingTop: number;
};

const Line = styled(TransitionGroup)<LineType>`
  display: flex;
  align-items: center;
  justify-content: start;
  flex-direction: column;

  ${({ $paddingTop }) => css`
    padding-top: ${$paddingTop}px;
  `}
`;

type LineUpRaceTrackPropsType = {
  className?: string;
  competitors: Array<number>;
  numberOfColumns: number;
  sportId: SportId;
};

const LineUpRaceTrack = ({ className = '', competitors, numberOfColumns, sportId }: LineUpRaceTrackPropsType) => {
  const lines = useMemo(() => Array.from({ length: numberOfColumns }, (_, index) => index + 1), [numberOfColumns]);

  const styles = LineUpRaceTrackBySportId[sportId];

  return (
    <Wrapper className={className}>
      <TrackWrapperBackground>
        <TrackBackground />
      </TrackWrapperBackground>
      <Track>
        <RaceStatusIndicator />
        <CarsWrapper $padding={styles?.carsWrapperPadding}>
          {lines.map((_, columnIndex) => (
            <Line
              key={`race-column-${columnIndex}`}
              $paddingTop={styles?.lineMarginTop ? columnIndex * styles.lineMarginTop : 0}
            >
              {competitors
                .filter((_, index) => index % numberOfColumns === columnIndex)
                .map((competitorId, index) => (
                  <LineUpRacerContainer
                    key={`racer-${columnIndex}-${index}`}
                    competitorId={competitorId}
                    position={index * numberOfColumns + (columnIndex + 1)}
                  />
                ))}
            </Line>
          ))}
        </CarsWrapper>
      </Track>
    </Wrapper>
  );
};

export default LineUpRaceTrack;
